import type { SupportTicket, TicketStatus } from './types'

export interface TicketSection {
  status: string
  label: string
  tickets: SupportTicket[]
}

// Exhaustive on purpose: a status added to the wire breaks this map, so nobody
// can ship one without naming its section.
const SECTION_LABELS: Record<TicketStatus, string> = {
  awaiting_user: 'Needs your reply',
  open: 'Open',
  resolved: 'Resolved',
}

// A ticket waiting on the user comes first: it is the only one that asks them
// to do something.
const SECTION_ORDER: TicketStatus[] = ['awaiting_user', 'open', 'resolved']

const UNRANKED = SECTION_ORDER.length

// A status this build does not know still reaches the user under its own name
// rather than vanishing from the list.
const sectionLabel = (status: string) =>
  SECTION_LABELS[status as TicketStatus] ?? status

const sectionRank = (status: string) => {
  const position = SECTION_ORDER.indexOf(status as TicketStatus)
  return position === -1 ? UNRANKED : position
}

const byNewestMessage = (first: SupportTicket, second: SupportTicket) =>
  second.lastMessageAt.localeCompare(first.lastMessageAt)

const bucketByStatus = (tickets: SupportTicket[]) => {
  const buckets = new Map<string, SupportTicket[]>()
  for (const ticket of tickets) {
    const bucket = buckets.get(ticket.status)
    if (bucket) bucket.push(ticket)
    else buckets.set(ticket.status, [ticket])
  }
  return buckets
}

export const groupTicketsByStatus = (
  tickets: SupportTicket[],
): TicketSection[] =>
  [...bucketByStatus(tickets).entries()]
    .sort(([first], [second]) => sectionRank(first) - sectionRank(second))
    .map(([status, sectionTickets]) => ({
      status,
      label: sectionLabel(status),
      tickets: sectionTickets.sort(byNewestMessage),
    }))
